import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Phone, MessageCircle, Mail, Shield, Clock, Car, CreditCard, MapPin, Users } from 'lucide-react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const faqCategories = [
    {
      title: "Booking & Reservations",
      icon: <Car className="text-blue-600" size={20} />,
      questions: [
        {
          q: "How can I book a cab with Shree Puja Travels?",
          a: "You can book a cab through multiple channels: 1) Call our 24/7 helpline at +91-XXXXXXXXXX, 2) Book via WhatsApp at +91-XXXXXXXXXX, 3) Use our website booking form, or 4) Visit our office in Hyderabad."
        },
        {
          q: "What information do I need to provide while booking?",
          a: "Please provide: 1) Pickup location and destination, 2) Date and time of travel, 3) Number of passengers and luggage, 4) Contact details, 5) Type of vehicle required, and 6) Any special requirements."
        },
        {
          q: "Can I book for someone else?",
          a: "Yes, you can book for family, friends, or colleagues. Please provide the passenger's contact details and any specific instructions for the driver."
        },
        {
          q: "Do you provide one-way and round-trip services?",
          a: "Yes, we offer both one-way and round-trip services. Round-trip bookings usually come with special discounts and driver waiting time is included."
        }
      ]
    },
    {
      title: "Pricing & Payments",
      icon: <CreditCard className="text-green-600" size={20} />,
      questions: [
        {
          q: "What is included in your pricing?",
          a: "Our pricing includes: 1) Vehicle rental, 2) Experienced driver, 3) Fuel charges, 4) Toll taxes, 5) State taxes, 6) Driver allowance. Exclusions: Parking charges (if applicable) and night charges (10 PM to 6 AM)."
        },
        {
          q: "Do you charge extra for night travel?",
          a: "Yes, we have a nominal night charge of 10-15% extra for travel between 10 PM to 6 AM. This is to ensure driver safety and availability during night hours."
        },
        {
          q: "What payment methods do you accept?",
          a: "We accept: 1) Cash (preferred), 2) UPI payments (Google Pay, PhonePe, Paytm), 3) Bank transfer, 4) Credit/Debit cards at our office, and 5) Corporate billing for regular customers."
        },
        {
          q: "Is there a cancellation fee?",
          a: "Cancellation charges apply based on timing: 1) More than 24 hours - No charge, 2) 12-24 hours before - 10% of booking amount, 3) Less than 12 hours - 25% of booking amount, 4) No show - 50% of booking amount."
        },
        {
          q: "Do you offer corporate discounts?",
          a: "Yes, we offer special corporate packages with discounted rates, monthly billing, and dedicated account managers for companies with regular travel needs."
        }
      ]
    },
    {
      title: "Fleet & Services",
      icon: <Users className="text-purple-600" size={20} />,
      questions: [
        {
          q: "What types of vehicles do you offer?",
          a: "We have a comprehensive fleet including: 1) Sedans (4-seaters), 2) SUVs (6-7 seater), 3) Innova Crysta (6/7 seater), 4) Tempo Travellers (9-26 seater), 5) Force Urbania (13-17 seater), 6) Luxury cars for special occasions."
        },
        {
          q: "Are your vehicles AC and well-maintained?",
          a: "Yes, all our vehicles are fully air-conditioned and undergo regular maintenance checks. We follow a strict maintenance schedule to ensure safety and comfort."
        },
        {
          q: "Do you provide outstation services?",
          a: "Yes, we specialize in outstation trips across South India. We offer package rates for popular destinations like Tirupati, Goa, Bangalore, Chennai, and more."
        },
        {
          q: "What is your airport transfer service?",
          a: "We provide 24/7 airport transfers to/from RGIA Hyderabad with: 1) Flight tracking, 2) Meet & greet service, 3) Fixed pricing, 4) Ample luggage space, and 5) Professional chauffeurs."
        },
        {
          q: "Do you provide vehicles for weddings and events?",
          a: "Yes, we offer special wedding packages with decorated vehicles, trained drivers, and flexible hours. We can provide multiple vehicles for guests and family members."
        }
      ]
    },
    {
      title: "Safety & Driver Information",
      icon: <Shield className="text-red-600" size={20} />,
      questions: [
        {
          q: "What safety measures do you follow?",
          a: "We ensure: 1) Verified and background-checked drivers, 2) GPS-tracked vehicles, 3) Regular vehicle sanitization, 4) First-aid kits in all vehicles, 5) Speed limit compliance, and 6) Emergency contact numbers."
        },
        {
          q: "Are your drivers experienced and trained?",
          a: "All our drivers have minimum 5 years of experience, valid licenses, and undergo regular training for customer service, route knowledge, and safe driving practices."
        },
        {
          q: "Do you provide female drivers?",
          a: "Yes, we have trained female drivers available on request. Please specify this requirement at the time of booking to ensure availability."
        },
        {
          q: "What if I have an emergency during the trip?",
          a: "You can contact our 24/7 emergency helpline. Our vehicles also have emergency contact numbers displayed, and drivers are trained to handle emergencies."
        },
        {
          q: "Is travel insurance included?",
          a: "All our vehicles are comprehensively insured. For personal accident coverage, we recommend passengers to have their own travel insurance."
        }
      ]
    },
    {
      title: "Policies & Terms",
      icon: <Clock className="text-amber-600" size={20} />,
      questions: [
        {
          q: "What is your waiting time policy?",
          a: "We provide free waiting time: 1) Airport pickups - 90 minutes from flight landing, 2) Railway stations - 60 minutes, 3) Local pickups - 15 minutes. After this, waiting charges apply."
        },
        {
          q: "Can I change my travel plan after booking?",
          a: "Yes, changes can be made subject to availability. Please inform us at least 6 hours in advance for route/time changes. Additional charges may apply."
        },
        {
          q: "What is your luggage policy?",
          a: "Standard luggage allowance is 1 suitcase + 1 handbag per passenger. For extra luggage, please inform in advance. Tempo travellers have dedicated luggage space."
        },
        {
          q: "Do you allow multiple stops during the journey?",
          a: "Yes, we allow reasonable stops for refreshments and restrooms. For multiple planned stops, please inform in advance. Additional charges may apply for extended waiting."
        },
        {
          q: "What is your child seat policy?",
          a: "Child seats are available on request with prior notice. Additional charges apply. Please specify the age and weight of the child while booking."
        }
      ]
    }
  ];

  const quickContact = [
    {
      icon: <Phone size={24} />,
      title: "Call Us",
      details: "+91-9100390049",
      subtext: "24/7 Available",
      color: "bg-blue-500"
    },
    {
      icon: <MessageCircle size={24} />,
      title: "WhatsApp",
      details: "+91-9100390049",
      subtext: "Quick Response",
      color: "bg-green-500"
    },
    {
      icon: <Mail size={24} />,
      title: "Email",
      details: "shreepujatraveldesk@gmail.com",
      subtext: "For Detailed Queries",
      color: "bg-purple-500"
    },
    {
      icon: <MapPin size={24} />,
      title: "Office",
      details: "Hyderabad, Telangana",
      subtext: "Visit Our Office",
      color: "bg-red-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h1>
          <div className="flex items-center justify-center mb-6">
            <div className="h-1 w-20 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full"></div>
            <span className="mx-4 text-gray-600 font-semibold">Shree Puja Travels</span>
            <div className="h-1 w-20 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full"></div>
          </div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Find answers to common questions about booking, pricing, services, and policies. 
            Still have questions? Contact our support team.
          </p>
        </div>

        {/* Quick Contact Cards */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Quick Contact Channels</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickContact.map((contact, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="flex items-center mb-4">
                  <div className={`${contact.color} p-3 rounded-full text-white mr-4`}>
                    {contact.icon}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-800">{contact.title}</h3>
                    <p className="text-sm text-gray-600">{contact.subtext}</p>
                  </div>
                </div>
                <p className="text-lg font-semibold text-gray-900">{contact.details}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-10">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for questions or keywords..."
                className="w-full px-6 py-4 pl-14 text-lg bg-white border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all duration-300 shadow-sm"
              />
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300">
                Search
              </button>
            </div>
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {faqCategories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
              {/* Category Header */}
              <div className="bg-gradient-to-r from-gray-50 to-white p-6 border-b border-gray-100">
                <div className="flex items-center">
                  {category.icon}
                  <h2 className="text-2xl font-bold text-gray-800 ml-3">{category.title}</h2>
                </div>
              </div>

              {/* Questions */}
              <div className="divide-y divide-gray-100">
                {category.questions.map((faq, faqIndex) => (
                  <div key={faqIndex} className="transition-all duration-300">
                    <button
                      onClick={() => toggleFAQ(`${categoryIndex}-${faqIndex}`)}
                      className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mt-1 mr-4">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        </div>
                        <span className="text-lg font-semibold text-gray-800 pr-4">{faq.q}</span>
                      </div>
                      {openIndex === `${categoryIndex}-${faqIndex}` ? (
                        <ChevronUp className="flex-shrink-0 text-blue-600" size={24} />
                      ) : (
                        <ChevronDown className="flex-shrink-0 text-gray-400" size={24} />
                      )}
                    </button>
                    
                    {openIndex === `${categoryIndex}-${faqIndex}` && (
                      <div className="px-6 pb-5 pl-12 animate-fadeIn">
                        <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                          <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                          {/* Additional Info for some FAQs */}
                          {category.title === "Booking & Reservations" && faqIndex === 0 && (
                            <div className="mt-4">
                              <button className="px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-green-700 transition-all duration-300 mr-3">
                                Book Now
                              </button>
                              <button className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-blue-700 transition-all duration-300">
                                View Fleet
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-16 mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 md:p-12 text-white"style={{ background: 'rgba(65, 1, 150, 1)' }}>
            <div className="flex flex-col lg:flex-row items-center justify-between">
              <div className="lg:w-2/3 mb-8 lg:mb-0">
                <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
                <p className="text-blue-100 text-lg mb-6">
                  Our customer support team is available 24/7 to assist you with any queries, 
                  special requests, or emergency assistance.
                </p>
                <div className="flex flex-wrap gap-4">
                  <button className="px-6 py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-gray-100 transition-colors duration-300">
                    Call Support Now
                  </button>
                  <button className="px-6 py-3 bg-transparent border-2 border-white text-white font-bold rounded-lg hover:bg-white/10 transition-colors duration-300">
                    WhatsApp Chat
                  </button>
                </div>
              </div>
              <div className="lg:w-1/3">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                  <h3 className="text-xl font-bold mb-4">Quick Response Guarantee</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <CheckCircle className="text-green-300 mr-2" size={18} />
                      <span>Response within 5 minutes</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="text-green-300 mr-2" size={18} />
                      <span>24/7 Availability</span>
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="text-green-300 mr-2" size={18} />
                      <span>Multi-language Support</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Popular Topics */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Popular Topics</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {[
              "Airport Transfers RGIA",
              "Hyderabad Outstation Tours",
              "Wedding Car Rental",
              "Corporate Travel Packages",
              "Tirupati Tour Packages",
              "Monthly Rental Plans"
            ].map((topic, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl border border-gray-200 p-4 hover:border-blue-300 hover:shadow-md transition-all duration-300 cursor-pointer"
              >
                <div className="flex items-center">
                  <div className="bg-blue-100 p-2 rounded-lg mr-3">
                    <Tag className="text-blue-600" size={18} />
                  </div>
                  <span className="font-medium text-gray-800">{topic}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer Note */}
        {/* <div className="text-center py-8 border-t border-gray-200">
          <p className="text-gray-600">
            For urgent bookings or assistance, call our 24/7 helpline: 
            <span className="font-bold text-blue-600 ml-2">+91-9100390049</span>
          </p>
          <p className="text-sm text-gray-500 mt-2">
            © {new Date().getFullYear()} Shree Puja Travels. All rights reserved.
          </p>
        </div> */}
      </div>

      {/* Custom CheckCircle Icon Component */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

// Custom CheckCircle Icon Component
const CheckCircle = ({ size = 24, className = "" }) => (
  <svg 
    className={className} 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
  </svg>
);

// Custom Tag Icon Component
const Tag = ({ size = 24, className = "" }) => (
  <svg 
    className={className} 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2"
  >
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);

export default FAQ;